import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Search } from "lucide-react";
import api, { errMsg, money } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const STATUS_STYLES = {
  draft: "border-slate-600 bg-slate-700/30 text-slate-300",
  sent: "border-blue-500/40 bg-blue-500/10 text-blue-300",
  accepted: "border-emerald-500/40 bg-emerald-500/10 text-emerald-300",
  declined: "border-red-500/40 bg-red-500/10 text-red-300",
  completed: "border-gold/40 bg-gold/10 text-gold",
};

export const StatusPill = ({ status }) => (
  <span
    data-testid={`status-pill-${status}`}
    className={`inline-flex items-center rounded-full border px-2.5 py-0.5 font-mono text-[10px] uppercase tracking-widest ${
      STATUS_STYLES[status] || STATUS_STYLES.draft
    }`}
  >
    {status}
  </span>
);

export default function Manifests() {
  const { company, quota } = useAuth();
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    api.get("/manifests").then(({ data }) => setItems(data)).catch((e) => setError(errMsg(e)));
  }, []);

  const filtered = items.filter((m) =>
    `${m.number} ${m.customer_name} ${m.pickup?.address} ${m.chauffeur_name}`.toLowerCase().includes(q.toLowerCase())
  );
  const quotaFull = quota?.limit ? quota.used >= quota.limit : false;

  return (
    <div data-testid="manifests-page" className="space-y-8">
      <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-end">
        <div>
          <p className="font-mono text-xs uppercase tracking-[0.28em] text-gold">Dispatch &amp; billing</p>
          <h1 className="mt-3 text-3xl font-bold tracking-tight text-slate-50">Trips</h1>
        </div>
        {quotaFull ? (
          <Button asChild className="bg-gold text-ink-900 hover:bg-gold-light">
            <Link to="/app/plans" data-testid="quota-upgrade-btn">Upgrade to add trips</Link>
          </Button>
        ) : (
          <Button asChild className="bg-gold text-ink-900 hover:bg-gold-light">
            <Link to="/app/manifests/new" data-testid="new-manifest-btn"><Plus className="mr-2 h-4 w-4" /> New trip</Link>
          </Button>
        )}
      </div>

      {quotaFull && (
        <div data-testid="quota-banner" className="rounded-lg border border-gold/30 bg-gold/10 p-4 text-sm text-gold">
          You've used all {quota.limit} trips included with the {company?.plan_name} plan this week.{" "}
          <Link to="/app/plans" className="underline">Go unlimited from $1.99/week</Link> — or wait until Monday
          for a fresh allowance.
        </div>
      )}
      {error && <p className="text-sm text-red-400">{error}</p>}

      <div className="relative max-w-md">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
        <Input
          data-testid="manifest-search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search trip, customer, chauffeur…"
          className="h-11 border-ink-600 bg-ink-800 pl-9 text-slate-100 focus-visible:ring-gold"
        />
      </div>

      <div className="overflow-hidden rounded-xl border border-ink-600 bg-ink-800">
        <div className="hidden grid-cols-12 gap-4 border-b border-ink-600 px-5 py-3 font-mono text-[10px] uppercase tracking-widest text-slate-500 md:grid">
          <span className="col-span-1">#</span>
          <span className="col-span-3">Customer</span>
          <span className="col-span-3">Pickup</span>
          <span className="col-span-2">When</span>
          <span className="col-span-1 text-right">Total</span>
          <span className="col-span-2 text-right">Status</span>
        </div>
        {filtered.length === 0 && (
          <p data-testid="manifests-empty" className="px-5 py-10 text-center text-sm text-slate-500">
            No trips found.
          </p>
        )}
        {filtered.map((m) => (
          <Link
            key={m.id}
            to={`/app/manifests/${m.id}`}
            data-testid={`manifest-row-${m.number}`}
            className="grid grid-cols-1 gap-2 border-b border-ink-600 px-5 py-4 transition-colors last:border-0 hover:bg-ink-700 md:grid-cols-12 md:items-center md:gap-4"
          >
            <span className="col-span-1 font-mono text-sm text-gold">{m.number}</span>
            <span className="col-span-3 truncate text-sm text-slate-100">{m.customer_name}</span>
            <span className="col-span-3 truncate text-xs text-slate-400">{m.pickup?.address || "—"}</span>
            <span className="col-span-2 text-xs text-slate-400">{m.trip_date} {m.pickup_time}</span>
            <span className="col-span-1 font-mono text-sm text-slate-200 md:text-right">{money(m.total_charge)}</span>
            <span className="col-span-2 md:text-right"><StatusPill status={m.status} /></span>
          </Link>
        ))}
      </div>
    </div>
  );
}
